import { Form, Input } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useLoaderData } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";
import { GetMeSuccessResponse } from "../../../types/api/get-me-success-response";
import { RoleEnum } from "../../../enums/role.enum";
import { setSelectedMenuItemKeys } from "../../../../../redux/reducer/antd-layout-info.reducer";
import { AlertMessageState, clearAlertMessage, showAlertMessage } from "../../../../../redux/reducer/alert-message.reducer";
import { AlertMessages } from "../../../../../enums/alert-messages.enum";
import { showLoadingAlertMessage } from "../../../../../utils/show-loading-alert-message/show-loading-alert-message";
import { networkCall } from "../../../../../utils/network-call/network-call";
import { showDefaultErrorMessages } from "../../../../../utils/show-default-error-message/show-default-error-messages";
import { DefaultErrorResponse } from "../../../../../types/api/error/default-error-response/default-error-respose.type";
import { ValidationErrorResponseData } from "../../../../../types/api/error/validation-error-response-data/validation-error-response-data.type.";
import { tranformAPIValidationErrorToAntDValidationMessage } from "../../../../../utils/validation/transform-api-validation-errors-to-antd-validation-messsages";
import { getImageUrl } from "../../../../../utils/image-helper/get-image-url";
import { CMSButton } from "../../../../../components/cms/button/cms-button.";

export const UserProfile = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const loaderData: any = useLoaderData();
    const [showChangePasswordFields, setShowChangePasswordFields] = useState(false);

    const getRoleName = useCallback((role: RoleEnum) => {
        switch (role) {
            case RoleEnum.Admin:
                return 'admin';
            case RoleEnum.CompanyAdmin:
                return 'cégvezető';
            case RoleEnum.Maintainer:
                return 'karbantartási vezető';
            case RoleEnum.User:
                return 'felhasználó';
            default:
        }
    }, []);

    useEffect(() => {
        dispatch(setSelectedMenuItemKeys([]));
    }, [dispatch]);

    useEffect(() => {
        if(loaderData.getMeResponse?.status === 200) {
            const res: AxiosResponse<GetMeSuccessResponse> = loaderData.getMeResponse;
            form.setFieldValue('name', res.data.user.name);
            form.setFieldValue('email', res.data.user.email);
            form.setFieldValue('role', getRoleName(res.data.user.role));
            form.setFieldValue('uniqueId', res.data.user.uniqueId);
        } else {
            dispatch(clearAlertMessage());
            dispatch(showAlertMessage({
                alertMessageText: AlertMessages.UnknownErrorTryAgain,
                alertMessageType: 'error'
            }));
        }
    }, [loaderData.getMeResponse, dispatch, form, getRoleName]);

    const handleFinish = async (values: any) => {
        showLoadingAlertMessage();

        let oldPassword: string | undefined = undefined;
        let newPassword: string | undefined = undefined;

        if(showChangePasswordFields) {
            if(values.newPassword !== values.newPasswordAgain){

                form.setFields([{
                        name: 'newPassword',
                        errors: ['A jelszavak nem egyeznek meg.']
                    },
                    {
                        name: 'newPasswordAgain',
                        errors: ['A jelszavak nem egyeznek meg.']
                    }
                ]);
    
                return;
            }

            oldPassword = values.oldPassword;
            newPassword = values.newPassword;
        }
        
        const response = await networkCall('/users/me', {
            method: 'PUT',
            body: {
                email: values.email,
                name: values.name,
                oldPassword,
                newPassword
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {
            alertMessageState.alertMessageText = AlertMessages.SavedSuccessfully;
            alertMessageState.alertMessageType = 'success';
            form.setFields([
                {
                    name: 'oldPassword',
                    value: ''
                },
                {
                    name: 'newPassword',
                    value: ''
                },
                {
                    name: 'newPasswordAgain',
                    value: ''
                }
            ]);
            setShowChangePasswordFields(false);
        } else if (response?.status === 404) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {
                case 7001:
                    alertMessageState.alertMessageText = 'A felhasználó nem található.'
                    break;
            }
        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 1000:
                    const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                    alertMessageState.alertMessageText = 'Hibásan megadott beviteli mezők.';
                    const validationErrors = tranformAPIValidationErrorToAntDValidationMessage(_res.data.data!);
                    form.setFields(validationErrors);
                    break;

            }

        }

        dispatch(showAlertMessage(alertMessageState));
    };

    return (
        <>
            <div className="page-title">Profil</div>
            <Form
                form={form}
                onFinish={handleFinish}
                layout='vertical'
            >
    
                <Form.Item
                    label={<b>Név</b>}
                    name='name'
                    rules={[{required: true, message: 'Név megadása kötelező!'}]}
                >
                    <Input className='box-shadow-4-4-8-0 border-0' />
                </Form.Item>

                <Form.Item
                    label={<b>Email cím</b>}
                    name='email'
                    rules={[{required: true, message: 'Email cím megadása kötelező!'}, {type: 'email', message: 'Helytelen email formátum!'}]}
                >
                    <Input className='box-shadow-4-4-8-0 border-0' />
                </Form.Item>

                {
                    loaderData.getMeResponse?.data?.user?.role === RoleEnum.User && (
                        <Form.Item
                            label={<b>Egyedi azonosító</b>}
                            name='uniqueId'
                        >
                            <Input className='box-shadow-4-4-8-0 border-0 fw-bold' disabled />
                        </Form.Item>
                    )
                }

                <Form.Item
                    label={<b>Jogosultsági szint</b>}
                    name='role'
                >
                    <Input className='box-shadow-4-4-8-0 border-0 fw-bold' disabled />
                </Form.Item>

                <CMSButton
                    text={ showChangePasswordFields ? 'Új jelszó kérésének elvetése' : 'Új jelszó kérése' }
                    iconSrc={getImageUrl('password-icon-white.png')}
                    onClick={() => setShowChangePasswordFields(!showChangePasswordFields)}
                    style={{ marginBottom: '25px' }}
                    type="blue"
                />

                {
                    showChangePasswordFields && (
                        <>
                            <Form.Item
                                label={<b>Régi jelszó</b>}
                                name='oldPassword'
                                rules={[{required: true, message: 'Jelszó megadása kötelező!'}]}
                            >
                                <Input.Password className='box-shadow-4-4-8-0 border-0' />
                            </Form.Item>

                            <Form.Item
                                label={<b>Új jelszó</b>}
                                name='newPassword'
                                rules={[{required: true, message: 'Jelszó megadása kötelező!'}]}
                            >
                                <Input.Password className='box-shadow-4-4-8-0 border-0' />
                            </Form.Item>

                            <Form.Item
                                label={<b>Új jelszó újra</b>}
                                name='newPasswordAgain'
                                rules={[{required: true, message: 'Jelszó megadása kötelező!'}]}
                            >
                                <Input.Password className='box-shadow-4-4-8-0 border-0' />
                            </Form.Item>
                        </>
                    )
                }

                <Form.Item className='text-end'>
                    <CMSButton text='Mentés' iconSrc={getImageUrl('Save-icon.png')} htmlType="submit" />
                </Form.Item>
                        
            </Form>
        </>
    );
};